import type { Ownership, ReviewInput } from '@skop-app/lib-domain/types'
import { ElementStatus, ReviewStatus, ReviewType } from '@skop-app/lib-types'

import {
  resolveTeamReviewWorkflowPolicy,
  type TeamReviewWorkflowPolicyV1,
} from './policy'

export type CreationElementType = 'option' | 'reference'
export type CreationSource = 'manual' | 'import' | 'duplicate' | 'resubmit'

export type CreationReviewPolicyResolution = {
  shouldCreateReview: boolean
  shouldNotifyAdmins: boolean
  elementStatus: ElementStatus
  reviewStatus?: ReviewStatus
}

const isOwnerOrganizationAdmin = ({
  ownership,
  listOrganizationID,
  isAdmin,
}: {
  ownership: Ownership
  listOrganizationID?: string
  isAdmin: boolean
}) =>
  isAdmin &&
  Boolean(ownership.organizationID) &&
  Boolean(listOrganizationID) &&
  ownership.organizationID === listOrganizationID

const shouldRequireReview = ({
  elementType,
  policy,
}: {
  elementType: CreationElementType
  policy: TeamReviewWorkflowPolicyV1 | null | undefined
}) => {
  const resolvedPolicy = resolveTeamReviewWorkflowPolicy(policy)
  return Boolean(
    elementType === 'option'
      ? resolvedPolicy.options?.manualCreation?.requiresReview
      : resolvedPolicy.references?.manualCreation?.requiresReview,
  )
}

export const resolveCreationReviewPolicy = (params: {
  elementType: CreationElementType
  source: CreationSource
  ownership: Ownership
  listOrganizationID?: string
  isAdmin: boolean
  isSkopAdmin: boolean
  teamReviewWorkflowPolicy: TeamReviewWorkflowPolicyV1 | null | undefined
}): CreationReviewPolicyResolution => {
  const {
    elementType,
    source,
    ownership,
    listOrganizationID,
    isAdmin,
    isSkopAdmin,
    teamReviewWorkflowPolicy,
  } = params

  if (source === 'resubmit') {
    return {
      shouldCreateReview: false,
      shouldNotifyAdmins: false,
      elementStatus: ElementStatus.PENDING,
    }
  }

  if (source === 'import' || source === 'duplicate') {
    return {
      shouldCreateReview: false,
      shouldNotifyAdmins: false,
      elementStatus: ElementStatus.ACCEPTED,
    }
  }

  const reviewRequired = shouldRequireReview({
    elementType,
    policy: teamReviewWorkflowPolicy,
  })
  const canBypassSelfReview =
    isSkopAdmin ||
    isOwnerOrganizationAdmin({
      ownership,
      listOrganizationID,
      isAdmin,
    })

  if (!reviewRequired || canBypassSelfReview) {
    return {
      shouldCreateReview: false,
      shouldNotifyAdmins: false,
      elementStatus: ElementStatus.ACCEPTED,
    }
  }

  return {
    shouldCreateReview: true,
    shouldNotifyAdmins: true,
    elementStatus: ElementStatus.PENDING,
    reviewStatus: ReviewStatus.PENDING,
  }
}

export const getCreationReviewType = (
  elementType: CreationElementType,
): ReviewType =>
  elementType === 'option'
    ? ReviewType.REVIEW_OPTION
    : ReviewType.REVIEW_REFERENCE

export const isCreationReviewInput = (
  review: ReviewInput | undefined,
): review is ReviewInput => Boolean(review?.id)
